/* eslint-disable no-param-reassign */
import type { RichTextRkpi, RichTextRkpiSpeed } from "@library-elements/text";
import { RKPI_SPEED_OPTIONS } from "../components/froala/constants";

export const getRkpiDataFromAttrs = (el: Element): RichTextRkpi => {
  let to = Number(el.getAttribute("data-rkpi-number-to"));
  let from = Number(el.getAttribute("data-rkpi-number-from"));
  const prefix = el.getAttribute("data-rkpi-prefix") ?? "";
  const suffix = el.getAttribute("data-rkpi-suffix") ?? "";
  const decimal = el.getAttribute("data-rkpi-decimal")
    ? JSON.parse(el.getAttribute("data-rkpi-decimal") as string)
    : false;
  const dataSpeed = el.getAttribute("data-rkpi-speed");
  let speed: RichTextRkpiSpeed = "medium";

  let delay: any = 0;
  let c: any = window
    .getComputedStyle(el)
    .getPropertyValue("--fr-rkpi-additional-delay");
  if (c) {
    c = c
      .replace("calc(", "")
      .replace(")", "")
      .replaceAll(" ", "")
      .replaceAll("ms", "")
      .split("+");
    c.forEach((e: string) => {
      const p = Number.parseFloat(e);
      if (!isNaN(p)) delay += p;
    });
  }
  if (isNaN(delay)) delay = 0;

  if (isNaN(to)) to = 100;
  if (isNaN(from)) from = 0;
  if (dataSpeed) {
    if (RKPI_SPEED_OPTIONS[dataSpeed as RichTextRkpiSpeed])
      speed = dataSpeed as RichTextRkpiSpeed;
    else {
      const parsedSpeed = Number.parseFloat(dataSpeed);
      if (!isNaN(parsedSpeed)) {
        for (const key in RKPI_SPEED_OPTIONS) {
          const val = RKPI_SPEED_OPTIONS[key as RichTextRkpiSpeed];
          if (val.duration === parsedSpeed) {
            speed = key as RichTextRkpiSpeed;
            break;
          }
        }
      }
    }
  }

  return {
    to: to as number,
    from: from as number,
    prefix,
    suffix,
    speed,
    delay,
    decimal,
  };
};

export const getRkpiDurationFromSpeed = (speed: RichTextRkpiSpeed): number => {
  if (!RKPI_SPEED_OPTIONS[speed]) return RKPI_SPEED_OPTIONS.medium.duration;
  return RKPI_SPEED_OPTIONS[speed].duration;
};

export const animateRkpi = (
  element: Element,
  start: number,
  end: number,
  duration: number,
  delay = 0,
  prefix: string | null = null,
  suffix: string | null = null,
  decimal = false,
) => {
  const digitsCount: number = countDigits(end);
  setTimeout(() => {
    let startTimestamp: number | null = null;
    const step = (timestamp: number) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      element.innerHTML = formatNumberWithCommas(
        Number((progress * (end - start) + start).toFixed(digitsCount)),
        decimal,
      ).toString();
      if (prefix) element.innerHTML = prefix + element.innerHTML;
      if (suffix) element.innerHTML = element.innerHTML + suffix;

      if (progress < 1) {
        window.requestAnimationFrame(step);
      }
    };
    window.requestAnimationFrame(step);
  }, delay);
};

export const rkpiElementV3ToV4 = (el: HTMLElement) => {
  el.removeAttribute("class");
  const { to, from, prefix, suffix, speed, decimal } = getRkpiDataFromAttrs(el);

  el.setAttribute("data-rkpi-active", "false");
  el.setAttribute("data-rkpi-number-to", to.toString());
  el.setAttribute("data-rkpi-number-from", from.toString());
  el.setAttribute("data-rkpi-prefix", prefix);
  el.setAttribute("data-rkpi-suffix", suffix);
  el.setAttribute("data-rkpi-speed", speed.toString());
  el.setAttribute("data-rkpi-decimals", decimal.toString());

  el.removeAttribute("data-from");
  el.removeAttribute("data-to");
  el.removeAttribute("data-speed");
  el.removeAttribute("data-prefix");
  el.removeAttribute("data-suffix");
  el.removeAttribute("data-decimal");
};

const countDigits = (number: number) => {
  const numberString = number.toString();
  if (numberString.indexOf(".") !== -1) {
    const decimalPart = numberString.split(".")[1];
    return decimalPart.length;
  }
  return 0;
};

const formatNumberWithCommas = (number: number, decimal: boolean) => {
  if (decimal) return number;
  const parts = number.toString().split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return parts.join(".");
};
